package top.wcpe.allincore.ui.runtime.host

import top.wcpe.allincore.ui.runtime.internal.concurrent.AtomicLongCounter
import top.wcpe.allincore.ui.runtime.internal.concurrent.SyncLock
import top.wcpe.allincore.ui.runtime.internal.concurrent.withLock
import top.wcpe.allincore.ui.runtime.internal.schedule.PlatformScheduler
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import top.wcpe.allincore.ui.runtime.theme.ToastStyle
import kotlin.concurrent.Volatile
import kotlin.jvm.JvmField

/**
 * Toast 队列管理器（M6-h Round 2）。
 *
 * 每个 IrSession 持有一个实例（screen 范围）。维护 FIFO 入队序列，同时最多显示 [MAX_ACTIVE] 个；
 * 超出排队等待。每个 toast 有独立 schedule 任务，duration > 0 时定时移除并尝试唤起队列下一个；
 * duration = 0 表示永久，直到 [dismiss] 显式移除。
 *
 * 简化决策：本轮不做淡入淡出动画（同 Round 1 modal）；duration 到时直接从活跃列表移除。动画统一留给 M6-i。
 *
 * 线程安全：active 与 pending 统一由私有锁保护；getActive 返回快照副本以避免迭代时修改。
 */
class ToastManager {

    /** 单个 toast 实例。仅暴露只读字段供渲染层使用。 */
    class ToastInstance internal constructor(
        @JvmField val id: Long,
        message: String?,
        kind: String?,
        @JvmField val duration: Long,
        // position 允许为 null：null 表示"DSL 未指定 position，由 ToastLayer 回退到 style.anchor()"。
        @JvmField val position: String?,
        @JvmField val createdAtMs: Long,
        /**
         * 解析后的视觉样式：由 `CallDispatcher.evalToast` 在主题中按 `style` 命名参数 / `kind` fallback
         * 解析后写入；为 null 时 `ToastLayer` 退化到 `ControlTheme.defaultToastStyle()`。
         */
        @JvmField val style: ToastStyle?,
    ) {
        @JvmField
        val message: String = message ?: ""

        @JvmField
        val kind: String = kind ?: "info"

        @JvmField
        var exitStartMs: Long = 0L
    }

    /** active 与 pending 的唯一同步锁，避免不同队列使用不同并发策略。 */
    private val lock = SyncLock()

    /** 活跃 toast；所有访问必须持有 lock 或通过快照。 */
    private val activeToasts = ArrayList<ToastInstance>()

    /** 排队等待的 toast；FIFO，超过 MAX_ACTIVE 时入队，所有访问必须持有 lock。 */
    private val pending = ArrayDeque<ToastInstance>()

    /** 单线程定时调度，作业是按 id 移除 toast；dispose 时统一关闭。 */
    private val scheduler = PlatformScheduler("toast-manager")

    /** id 生成器：incrementAndGet 首值为 1，与迁移前 AtomicLong(1).getAndIncrement 序列一致。 */
    private val idGen = AtomicLongCounter(0L)

    @Volatile
    private var disposed = false

    /** 暂停标志：暂停期间拒绝新 toast 显示。 */
    @Volatile
    private var paused = false

    /** 入队 / 显示一个 toast，使用解析后的 [ToastStyle] 描述视觉。线程安全。 */
    fun show(message: String?, kind: String?, duration: Long, position: String?, style: ToastStyle?) {
        lock.withLock {
            if (disposed || paused) {
                return
            }
            val inst = ToastInstance(
                idGen.incrementAndGet(),
                message,
                kind,
                duration,
                position,
                currentTimeMillis(),
                style,
            )
            if (activeShowingCount() < MAX_ACTIVE) {
                activeToasts.add(inst)
                schedule(inst)
            } else {
                pending.addLast(inst)
            }
        }
    }

    /**
     * 兼容旧入口：未提供 style 时按 null 透传，ToastLayer 在渲染时退化到
     * `ControlTheme.defaultToastStyle()` 推导。
     */
    fun show(message: String?, kind: String?, duration: Long, position: String?) {
        show(message, kind, duration, position, null)
    }

    /** 显式按 id 让活跃 toast 进入退出阶段，并尝试激活等待者。 */
    fun dismiss(id: Long) {
        dismissAt(id, currentTimeMillis())
    }

    /** 返回当前活跃 toast 的快照；调用方可安全迭代。 */
    val active: List<ToastInstance> get() = lock.withLock { ArrayList(activeToasts) }

    /** 单测：模拟时间推进，先进入退出阶段，淡出期满后移除。生产路径不调用。 */
    fun tickForTest(nowMs: Long) {
        lock.withLock {
            val expired = activeToasts
                .filter { it.exitStartMs <= 0L && it.duration > 0L && nowMs - it.createdAtMs >= it.duration }
                .map { it.id }
            for (id in expired) {
                val target = findActive(id)
                if (target != null && target.exitStartMs <= 0L) {
                    target.exitStartMs = nowMs
                }
            }
            activeToasts.removeAll { it.exitStartMs > 0L && nowMs - it.exitStartMs >= EXIT_MS }
            promotePending()
        }
    }

    /** 销毁：清空所有 toast、取消所有定时任务。session 销毁时调用。 */
    fun dispose() {
        lock.withLock {
            disposed = true
            activeToasts.clear()
            pending.clear()
        }
        scheduler.shutdownNow()
    }

    /** 暂停：拒绝新 toast 显示。 */
    fun pause() {
        lock.withLock { paused = true }
    }

    /** 恢复：允许新 toast 显示。 */
    fun resume() {
        lock.withLock { paused = false }
    }

    /** 查询是否处于暂停状态。 */
    fun isPaused(): Boolean = lock.withLock { paused }

    // ---- 内部实现（均在持有 lock 的上下文中调用） ----

    /** 安排到期任务；duration <= 0 视为永久，不调度。 */
    private fun schedule(inst: ToastInstance) {
        if (inst.duration <= 0L || disposed) {
            return
        }
        scheduler.schedule({ dismiss(inst.id) }, inst.duration)
    }

    private fun dismissAt(id: Long, nowMs: Long) {
        lock.withLock {
            val target = findActive(id)
            if (target == null || target.exitStartMs > 0L) {
                return
            }
            target.exitStartMs = nowMs
            scheduleRemove(id)
            promotePending()
        }
    }

    private fun findActive(id: Long): ToastInstance? = activeToasts.firstOrNull { it.id == id }

    private fun activeShowingCount(): Int = activeToasts.count { it.exitStartMs <= 0L }

    private fun promotePending() {
        while (canPromote()) {
            val next = pending.removeFirst()
            activeToasts.add(next)
            schedule(next)
        }
    }

    /** 是否还能从 pending 提升下一个到 active（拆分条件规避 detekt ComplexCondition）。 */
    private fun canPromote(): Boolean {
        if (disposed || paused) {
            return false
        }
        return pending.isNotEmpty() && activeShowingCount() < MAX_ACTIVE
    }

    private fun scheduleRemove(id: Long) {
        if (disposed) {
            return
        }
        scheduler.schedule({ removeAfterExit(id) }, EXIT_MS)
    }

    private fun removeAfterExit(id: Long) {
        lock.withLock {
            val removed = activeToasts.removeAll { it.id == id && it.exitStartMs > 0L }
            if (removed) {
                promotePending()
            }
        }
    }

    companion object {
        /** 同时显示的最大非退出 toast 数。超出则排队。 */
        const val MAX_ACTIVE: Int = 3

        /** toast 淡出时长（毫秒）。 */
        const val EXIT_MS: Long = 150L
    }
}
